import Visualizer from './visualizer';
import debounce from 'debounce';
import _round from 'lodash.round';
import { hierarchy, treemap } from 'd3-hierarchy';
import { t_analyzed_episodes_ss, t_analyzed_episode_ss, t_streak } from '../common/types';
import { SPEAKER } from '../common/speakers';

/**
 * CSS.
 */
import './index.scss';

/**
 * Datasets.
 */
import DATA from '../../build/dataset-web.json';

/**
 * Images.
 */
import ttLogo from '../../assets/tt-logo-white.png';
import peaceSign from '../../assets/peace-sign.svg';

export default class TrashTasteStory {
    holderId: string;
    holderDOM: HTMLElement;
    visualizer: Visualizer;
    episodeChartsHolderId: string;
    data: t_analyzed_episodes_ss;
    nrFormatter: Intl.NumberFormat;
    hierarchyDataTreemap: d3.HierarchyNode<any>;
    version: string;

    constructor(holderId: string) {
        this.data = DATA as unknown as t_analyzed_episodes_ss;

        this.holderId = holderId;
        this.holderDOM = document.getElementById(this.holderId);

        this.episodeChartsHolderId = 'tt-episode-chart';

        this.nrFormatter = new Intl.NumberFormat('en-US');

        this.hierarchyDataTreemap = this.createHierarchyDataTreemap();

        this.version = '1.0.1';
    }

    init() {
        this.createDomElements();

        this.visualizer = new Visualizer(this.episodeChartsHolderId, this.data);
        this.visualizer.init();

        this.createTreemap();
        this.bindEvents();
    }

    bindEvents() {
        // Create debounced resize.
        const debResize = debounce(this.resize.bind(this), 500);

        window.addEventListener('resize', debResize);
    }

    resize() {
        // Changing this breakpoint needs to be done in sync with the css code.
        if (window.innerWidth > 620) {
            this.visualizer.redrawCanvases();
        } else {
            this.visualizer.redrawMobileCanvases();
        }

        this.createTreemap();
    }

    /**
     * Template.
     */
    createDomElements() {
        const template = `
            <div id="tt-poster">
                ${this.createSocialLinks()}
                ${this.createHeaderHtml()}
                ${this.createInfoBlockHtml()}
                ${this.createSummaryBlockHtml()}

                <div class="tt-treemap-holder"></div>

                ${this.createStreakHtml()}
                ${this.createEpisodeChartHolderHtml()}

                ${this.createMethodologyHtml()}

                ${this.createFooterHtml()}
            </div>
        `;

        this.holderDOM.innerHTML = template;
    }

    createSocialLinks(): string {
        return `
            <div class="tt-copyright">
                <a href="https://dnlbln.com/" target="_blank">DNLBLN.COM</a>

                <a href="https://twitter.com/danijelbeljan" target="_blank">@danijel.beljan</a>

                <a href="https://www.youtube.com/channel/UCyyRGRkAoM8MbbAW4w8NtMg" target="_blank">Youtube</a>

                v${this.version}
            </div>
        `;
    }

    createHeaderHtml(): string {
        return `
            <div class="tt-header">

                <div class="tt-header-title">
                    A DATA ANALYSIS INTO THE USAGE OF<br>THE FILLER WORD <u>LIKE</u> ON THE
                </div>

                <a class="tt-yt-link" href="https://www.youtube.com/channel/UCcmxOGYGF51T1XsqQLewGtQ" target="_blank">
                    <img class="tt-logo" src="${ttLogo}"/>
                </a>

                <div class="tt-header-podcast">Podcast</div>
            </div>
        `
    }

    createInfoBlockHtml(): string {
        const totalHours = _round(this.data.totalLengthEpisodesInHours);

        return `
            <div class="tt-info-block">
                <div>
                    <a href="https://www.youtube.com/channel/UCcmxOGYGF51T1XsqQLewGtQ" target="_blank">Trash Taste</a> is an anime podcast exploring anime, manga, and otaku culture with top anime YouTubers Joey from the The Anime Man, Garnt from Gigguk, and Connor from Cdawgva and occasionally special guests.
                </div>

                <div>
                    In <a href="https://youtu.be/Zk8QMtjnRj0?t=745" target="_blank">episode 28</a>, the boys mentioned their over-usage of filler words, and more specifically the word 'LIKE'. After watching that episode, I wondered how frequent their usage of the filler word actually was, so I did an empirical study.
                </div>

                <div>
                Using IBM Watson\'s text-to-speech followed by a manual review process, I analysed over ${totalHours} hours of content and created a comprehensive dataset on their usage of the filler word. Correct usages of the word have been excluded.
                </div>
            </div>
        `
    }

    createEpisodeChartHolderHtml(): string {
        return `
            <h2>Episodes</h2>
            <p style="text-align: center">Each white dash is an occurrence of 'like' being used as a filler word.</p>

            <div id="${this.episodeChartsHolderId}"></div>

            <div id="${this.episodeChartsHolderId}-mobile"></div>
        `
    }

    createSummaryBlockHtml() {
        const totalHours = _round(this.data.totalLengthEpisodesInHours);
        const amountOfEpisodes = this.data.episodes.length;
        const totalLikes = this.nrFormatter.format(this.data.totalLikes);
        const lph = this.data.averageLikesPerHour;
        const evsl = this.data.averageLikeEverySecond;

        // Connor.
        const cla = this.nrFormatter.format(this.data.likesPerPerson.connor.count);
        const clap = _round(this.data.likesPerPerson.connor.percentageOfTotal);
        const cales = this.data.likesPerPerson.connor.averageLikeEverySecond;

        // Garnt.
        const gla = this.nrFormatter.format(this.data.likesPerPerson.garnt.count);
        const glap = _round(this.data.likesPerPerson.garnt.percentageOfTotal);
        const gales = this.data.likesPerPerson.garnt.averageLikeEverySecond;

        // Joe.
        const jla = this.nrFormatter.format(this.data.likesPerPerson.joe.count);
        const jlap = _round(this.data.likesPerPerson.joe.percentageOfTotal);
        const jales = this.data.likesPerPerson.joe.averageLikeEverySecond;

        return `<div class="tt-summary">
            <h2>Summary</h2>
            <p>
                Over the course of ${this.hw(amountOfEpisodes)} podcast sessions (${this.hw(totalHours + '+')} hours of audio content) the boys have said the word ${this.hw('LIKE')} as filler ${this.hw(totalLikes)} times.
                This comes down to an average of ${this.hw(lph)} likes / hour, or on average, they say 'like', as filler, every ${this.hw(evsl)} seconds.
            <p>
            <p>
                Across all episodes, ${this.hw('Connor')} used the word 'like' as filler ${this.hw(cla + ' (' + clap + '%)')} times, ${this.hw('Garnt')} ${this.hw(gla + ' (' + glap + '%)')}, and ${this.hw('Joe')} ${this.hw(jla + ' (' + jlap + '%)')}. ${this.hw('Connor')} says, on average, like once every ${this.hw(cales)} seconds, ${this.hw('Garnt')} once every ${this.hw(gales)} seconds and ${this.hw('Joe')} once every ${this.hw(jales)} seconds.
            </p>
        </div>`;
    }

    createStreakHtml(): string {
        const entries = Object.entries(this.data.longestStreaksPerSpeaker)

        // Sort from most likes to least.
        entries.sort((a, b) => {
            return b[1][0].likeCount - a[1][0].likeCount
        });

        const speaker = entries[0][0] as SPEAKER
        const streak = entries[0][1][0];

        const epId = streak.episodeId;
        const lcount = streak.likeCount;
        const lsName = this.uppercaseFirstLetter(speaker);
        const startT = streak.startTimestamp;
        const endT = streak.endTimestamp;
        const ep = this.findEpisodeById(epId);
        const ytLink = `${ep.youtubeLink}&t=${startT}`;
        const streakDuration = endT - startT;
        const ales = streak.averageLikeEverySecond;

        const secondStreakHtml = this.createStreakTextHtml(entries[1][0] as SPEAKER, entries[1][1]);
        const thirdStreakHtml = this.createStreakTextHtml(entries[2][0] as SPEAKER, entries[2][1]);

        const lsHtml = `<p>
            The longest streak award goes to ${this.hw(lsName)} who said 'like' as filler ${this.hw(lcount)} times in succession in episode ${this.hw(epId)} from timestamp <a href="${ytLink}" target="_blank">${startT} to ${endT}</a>. This is an average of 1 'like' every ${this.hw(ales)} seconds over a period of ${this.hw(streakDuration)} seconds.
        </p>`

        return `
            <div class="tt-streaks">
                <h2>Streaks</h2>

                <p>Streaks are periods of time where a speaker successively says 'like' as a filler word without being interrupted by another speaker also using 'like' as filler.
                ${lsHtml}

                ${secondStreakHtml}
                ${thirdStreakHtml}
            </div>
        `;
    }

    createStreakTextHtml(speaker: SPEAKER, streaks: t_streak[]): string {
        const name = this.uppercaseFirstLetter(speaker);

        // Always choose first streak. Otherwise text get's too complicated.
        const streak = streaks[0];
        const likes = streak.likeCount;
        const epId = streak.episodeId;
        const startT = streak.startTimestamp;
        const endT = streak.endTimestamp;
        const streakDuration = endT - startT;
        const ales = streak.averageLikeEverySecond;

        const ep = this.findEpisodeById(epId);

        const ytLink = `${ep.youtubeLink}&t=${startT}`;

        return `<p>
            ${this.hw(name + "'s")} longest streak was in episode ${this.hw(epId)} from timestamp <a href="${ytLink}" target="_blank">${startT} to ${endT}</a>, where he said 'like' ${this.hw(likes)} times in succession. This averages out to 1 ${this.hw("'like'")} every ${this.hw(ales)} seconds over a period of ${this.hw(streakDuration)} seconds.
        </p>`;
    }

    createMethodologyHtml(): string {
        const totalHours = _round(this.data.totalLengthEpisodesInHours);

        return `
            <div class="tt-methodology">
            <p>
                    If you liked this article check out my <a href="https://dnlbln.com/" target="_blank">website</a> for more work, or get in touch via <a href="https://twitter.com/danijelbeljan" target="_blank">twitter</a>/mail if you are interested in working with me, or if you are just doing cool stuff and want to collab.
                </p>

                <p>${this.hw('~ D.')}</p>

                <h2>Methodology</h2>

                <ol>
                    <li>Download all podcast episodes.</li>
                    <li>Prepare audio files and send to IBM Watson.</li>
                    <li>Load transcribed files from IBM Watson into custom made app for tagging.</li>
                    <li>Go through each found occurrence and manually listen and tag for correct context.</li>
                    <li>Analyse tagged dataset and write draft.</li>
                    <li>Code a web-based story based on the drafted article.</li>
                </ol>

                <p>
                Obviously listening to ${totalHours}+ hours of audio content and manually tagging will incur some errors. IBM Watson's speech-to-text is also not perfect and it tends to miss occurrences in the audio. In my estimate I believe the error rate to be around ${this.hw('1-3%')}.
                </p>
                <p>
                    Summed up like that it seems much easier than it is. During each step there were issues and roadblocks. The whole project took me about ${this.hw('70-80')} hours from start to finish. It was quite an interesting journey, One I am interested in talking about more on my <a href="https://www.youtube.com/channel/UCyyRGRkAoM8MbbAW4w8NtMg" target="_blank">youtube channel</a> (once I get it up and going in the near future). I will most likely do a project break down with a detailed look at what I did right and what was more difficult than expected.
                </p>
            </div>
        `;
    }

    createFooterHtml(): string {
        return `
            <div class="tt-footer">
                <img class="tt-peace-sign" src="${peaceSign}" />
            </div>
        `;
    }

    /**
     * Dataviz.
     */

    createHierarchyDataTreemap(): d3.HierarchyNode<any> {
        const joe = {
            name: 'joe',
            children: []
        };

        const connor = {
            name: 'connor',
            children: []
        };

        const garnt = {
            name: 'garnt',
            children: []
        };

        const map = {
            [SPEAKER.CONNOR]: connor,
            [SPEAKER.JOE]: joe,
            [SPEAKER.GARNT]: garnt
        };

        const children = [connor, garnt, joe];

        const d = {
            name: 'all',
            children: children
        };

        for (let i = 0; i < this.data.episodes.length; i++) {
            const episode = this.data.episodes[i];
            const likesPerPerson = episode.likesPerPerson;

            for (let name in likesPerPerson) {
                const speaker = name as SPEAKER;

                map[speaker].children.push({
                    value: likesPerPerson[name].count,
                    name: episode.id
                });
            }
        }

        return hierarchy(d)
            .sum(d => {
                // @ts-ignore
                return d.value;
            })
            .sort((a, b) => b.value - a.value);
    }

    /**
     * Todo:
     *  - Break up into smaller chunks.
     *  - Annotate instead of ts-ignore.
     *  - Add animations on resize?
     *  - Make treemap zoomable?
     */
    createTreemap() {
        const posterDom = document.getElementById('tt-poster');
        const innerDim = this.innerDimensions(posterDom);
        let treemapHeight = window.innerHeight - 150;

        if (treemapHeight > 800) {
            treemapHeight = 800;
        } else if (treemapHeight < 500) {
            treemapHeight = 500;
        }

        const treemapWidth = innerDim.width;

        const trGen = treemap()
            .size([treemapWidth, treemapHeight])
            .paddingInner(0)
            .round(true);

        const rootNode = trGen(this.hierarchyDataTreemap);

        let html = '';

        for (let i = 0; i < rootNode.children.length; i++) {
            let child = rootNode.children[i];

            const w = child.x1 - child.x0;
            const h = child.y1 - child.y0;

            const style = [
                `left: ${child.x0}px`,
                `right: ${child.x1}px`,
                `top: ${child.y0}px`,
                `bottom: ${child.y1}px`,
                `width: ${w}px`,
                `height: ${h}px`,
            ];

            // @ts-ignore.
            const percentage = _round(this.data.likesPerPerson[child.data.name].percentageOfTotal);
            // @ts-ignore
            const name = child.data.name.charAt(0).toUpperCase();

            // @ts-ignore
            const name2 = child.data.name;

            html += `
                <div class="tt-tm-tile tt-tm-tile-head" style="${style.join(';')}">
                    <div class="tt-tm-sp-name tt-tm-sp-name-${name}">${name} (${percentage}%)</div>
                </div>
            `;

            for (let j = 0; j < child.children.length; j++) {
                const subChild = child.children[j];

                const w = subChild.x1 - subChild.x0;
                const h = subChild.y1 - subChild.y0;

                const style = [
                    `left: ${subChild.x0}px`,
                    `right: ${subChild.x1}px`,
                    `top: ${subChild.y0}px`,
                    `bottom: ${subChild.y1}px`,
                    `width: ${w}px`,
                    `height: ${h}px`,
                ];

                let styleLikeCount = [];

                // @ts-ignore
                const likes = subChild.data.value;
                // @ts-ignore
                const epId = subChild.data.name;

                html += `
                    <div class="tt-tm-tile tt-tm-tile-${name2}" style="${style.join(';')}">
                        <div class="tt-tm-tile-data">
                        <div class="tt-tm-epid">#${epId}</div>
                            <div class="tt-tm-likes" style="${styleLikeCount.join(';')}">${likes}</div>
                        </div>
                    </div>
                `;

            }
        }

        const ttDom = this.holderDOM.getElementsByClassName('tt-treemap-holder')[0];
        ttDom.innerHTML = html;
        // @ts-ignore
        ttDom.style.height = `${treemapHeight}px`;
    }

    /**
     * Helpers.
     */

    hw(text: string | number) {
        return `<span class="tt-hw">${text}</span>`
    }

    uppercaseFirstLetter(text: string): string {
        return text.charAt(0).toUpperCase() + text.substr(1);
    }

    innerDimensions(node) {
        var computedStyle = getComputedStyle(node);

        let width = node.clientWidth; // width with padding.
        let height = node.clientHeight; // height with padding.

        height -= parseFloat(computedStyle.paddingTop) + parseFloat(computedStyle.paddingBottom);
        width -= parseFloat(computedStyle.paddingLeft) + parseFloat(computedStyle.paddingRight);

        return { height, width }
    }

    findEpisodeById(id): t_analyzed_episode_ss {
        for (let i = 0; i < this.data.episodes.length; i++) {
            if (this.data.episodes[i].id === id) {
                return this.data.episodes[i];
            }
        }
    }
}
